import { useMemo, useState } from 'react';
import type { Payload, WindowKey } from '../api/types';
import { buildStories, KIND_LABEL, W_LABEL, type Story, type StoryKind } from '../lib/stories';
import { Rail } from '../ui/charts';

/** `**bold**` in generated prose, rendered as <strong>. Nothing else is parsed. */
export function Rich({ text }: { text: string }) {
  const parts = text.split(/\*\*(.+?)\*\*/g);
  return <>{parts.map((p, i) => (i % 2 ? <strong key={i}>{p}</strong> : p))}</>;
}

/** A story's cover: its headline figure over a rail of the tracker readings,
    on the site's hero art. Drawn from the data, so it is never out of date. */
export function Cover({ story }: { story: Story }) {
  return (
    <div className={`cover cover-${story.kind}${story.cover ? ' has-art' : ''}`} aria-hidden
      style={story.cover ? { ['--cover-img' as string]: `url("${story.cover}")` } : undefined}>
      <span className="cover-figure">{story.figure}</span>
      <span className="cover-label">{story.figureLabel}</span>
      {story.readings.length >= 2 && <div className="cover-rail"><Rail readings={story.readings} compact /></div>}
    </div>
  );
}

export function Card({ story, win }: { story: Story; win: WindowKey }) {
  return (
    <a className="tile report-card" href={`/report?id=${encodeURIComponent(story.id)}&w=${win}`}>
      <Cover story={story} />
      <div className="report-copy">
        <span className="story-kicker"><i className="live-dot" aria-hidden />{story.kicker}</span>
        <h2>{story.title}</h2>
        <p>{story.dek}</p>
        <small>{story.minutes} min read · last {W_LABEL[win]}</small>
      </div>
    </a>
  );
}

export function Reports({ data, win }: { data: Payload; win: WindowKey }) {
  const stories = useMemo(() => buildStories(data, win), [data, win]);
  const [filter, setFilter] = useState<'all' | StoryKind>('all');
  const kinds = (Object.keys(KIND_LABEL) as StoryKind[]).filter((k) => stories.some((s) => s.kind === k));
  const shown = filter === 'all' ? stories : stories.filter((s) => s.kind === filter);
  const collected = new Date(data.scrapedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div className="page reports">
      <section className="tile reports-head banner-head banner-reports">
        <span className="story-kicker"><i className="live-dot" aria-hidden />Reports · rewritten every collection</span>
        <h1 className="section-title">Where the trackers disagree</h1>
        <p>
          Stories written from the {collected} collection. Every figure in them is what a tracker published.
          None of them is a verdict on which tracker is right.
        </p>
        <div className="report-filter" role="group" aria-label="Filter reports">
          <button type="button" className="chip" aria-pressed={filter === 'all'} onClick={() => setFilter('all')}>
            All <span>{stories.length}</span>
          </button>
          {kinds.map((k) => (
            <button key={k} type="button" className="chip" aria-pressed={filter === k} onClick={() => setFilter(k)}>
              {KIND_LABEL[k]} <span>{stories.filter((s) => s.kind === k).length}</span>
            </button>
          ))}
        </div>
      </section>

      {shown.length ? (
        <ul className="report-list">
          {shown.map((s, i) => {
            // The first story leads. The grid is three across on wide screens and
            // two below that; whichever it is, the last card fills the gap its row
            // would otherwise leave.
            const featured = i === 0;
            const last = !featured && i === shown.length - 1;
            const rest = shown.length - 1;
            const cls = featured ? 'featured' : [
              last && rest % 2 === 1 ? 'fill2' : '',
              last && rest % 3 === 1 ? 'fill3-all' : '',
              last && rest % 3 === 2 ? 'fill3-two' : '',
            ].filter(Boolean).join(' ');
            return (
              <li key={s.id} className={cls}>
                <Card story={s} win={win} />
              </li>
            );
          })}
        </ul>
      ) : <div className="tile"><p className="empty">No stories in this window yet.</p></div>}
    </div>
  );
}
